// Pill-shaped marketplace search bar — Airbnb-style with a hard
// split between mobile and desktop behavior:
//
//   <md   → single full-width tappable pill rendering placeholder
//           text ("Caută orașul, salonul sau serviciul..."). Tap
//           opens the WhereModal — the same expanded "Where?" flow
//           native marketplace apps use to keep the input area
//           uncluttered on phones.
//   md+   → three-segment expanded pill (Locație | Tip serviciu |
//           Caută) split by hairline dividers, with a round orange
//           submit on the right. The filter trigger lives in the
//           results bar, not here.
//
// Each shape writes back to ?city= / ?category= / ?q= so the page's
// fetch effect picks up the change automatically.

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiSearch, FiMapPin, FiType } from 'react-icons/fi';
import LocationPopover, { LOCATION_POPOVER_OPTION_COUNT } from './LocationPopover';
import useComboboxNav from '../../hooks/useComboboxNav';

// Free-text search only: Locație + Nume salon. Service-category filtering lives
// exclusively in the Filtre modal (and the category pills on the list view) —
// the old in-bar "Tip serviciu" dropdown was redundant with both and was removed.
export default function PillSearchBar({ onOpenWhere, compact = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCity = searchParams.get('city') || '';
  const urlQuery = searchParams.get('q') || '';

  const [draftQuery, setDraftQuery] = useState(urlQuery);
  useEffect(() => { setDraftQuery(urlQuery); }, [urlQuery]);

  // Draft city — typing only updates the draft so we don't refire
  // the fetch effect on every keystroke. URL gets the value on
  // blur, Enter, datalist selection, or a Caută submit.
  const [draftCity, setDraftCity] = useState(urlCity);
  useEffect(() => { setDraftCity(urlCity); }, [urlCity]);

  const commitParam = (key, value) => {
    // Two-layer comparison so the URL can be cleaned and the page
    // position protected, independently:
    //
    //   - Raw early-return: if the trimmed input is byte-identical
    //     to what the URL already shows (whitespace included), there
    //     is literally nothing to write. Covers the "user re-submits
    //     the same dirty URL" case AND `commitParam(key, null)` when
    //     `key` isn't set (`null` → `''` matches the empty rawCurrent).
    //
    //   - Trimmed equality inside setSearchParams: decides whether to
    //     reset ?page=. The visitor's pagination position is preserved
    //     when the semantic value didn't actually change — even if the
    //     URL is being cleaned of whitespace (`?city=Cluj ` → `?city=Cluj`
    //     stays on page 3 instead of bouncing back to 1).
    //
    // The `?? ''` normalises null/undefined so the early-return
    // comparison doesn't false-negative against the empty-string
    // rawCurrent.
    const trimmed = (typeof value === 'string' ? value.trim() : value) ?? '';
    const rawCurrent = searchParams.get(key) || '';
    const current = rawCurrent.trim();
    if (trimmed === rawCurrent) return;
    // Page-reset decision uses case-insensitive comparison. Backend's
    // city + free-text filters are case-insensitive at the SQL layer
    // (mode: 'insensitive'), so re-typing "Cluj" over a URL of "cluj"
    // isn't a semantic change — only the visual capitalisation
    // differs. Resetting ?page= for capitalization-only normalisation
    // would bounce the visitor off page 3 without changing what the
    // results are. The semantically-changed check keeps everyone on
    // the same row of salons.
    const trimmedFolded = typeof trimmed === 'string' ? trimmed.toLowerCase() : trimmed;
    const currentFolded = current.toLowerCase();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (trimmed) next.set(key, trimmed); else next.delete(key);
      // `key !== 'page'` guard: this helper is built to commit a
      // FILTER change and reset pagination as a side-effect. If a
      // future caller ever passed `commitParam('page', '5')`, the
      // page-reset clause below would delete `?page=` immediately
      // after the line above set it — a silent self-cancelling write.
      // The guard makes the contract self-enforcing instead of
      // relying on callers to know "this is for filters only".
      if (key !== 'page' && trimmedFolded !== currentFolded) next.delete('page');
      return next;
    });
  };

  // Location popover state. Opens on input focus, closes on outside
  // click, Escape, or after a destination is picked.
  const [locOpen, setLocOpen] = useState(false);
  const locRef = useRef(null);
  const popoverRef = useRef(null);

  // Combobox keyboard nav — arrow/home/end navigate options, Enter
  // activates the highlighted one via popoverRef.activate(idx). The
  // hook resets activeIndex when the popover closes, so re-opening
  // starts with no highlight.
  const { activeIndex, activeId, onKeyDown: onComboKeyDown, setActiveIndex } =
    useComboboxNav({
      isOpen: locOpen,
      optionCount: LOCATION_POPOVER_OPTION_COUNT,
      listboxId: 'pill-city-listbox',
      onActivate: (idx) => popoverRef.current?.activate(idx),
      onClose: () => setLocOpen(false),
    });

  useEffect(() => {
    if (!locOpen) return;
    const onDocClick = (e) => {
      if (locRef.current && !locRef.current.contains(e.target)) setLocOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
    };
  }, [locOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    commitParam('q', draftQuery);
  };

  // Surface a short summary on the mobile pill — if the user has
  // already picked a city or query, show it instead of the generic
  // placeholder so the pill confirms what's currently filtered.
  const mobileSummary = urlCity || urlQuery || 'Caută orașul, salonul sau serviciul...';

  return (
    <div className="mx-auto w-full max-w-3xl">
      {/* ── Mobile pill (button) ── */}
      <button
        type="button"
        onClick={onOpenWhere}
        className="flex h-14 w-full items-center gap-3 rounded-full border border-ink/15 bg-cream-50 px-5 shadow-lg transition-shadow hover:shadow-xl md:hidden"
      >
        <FiSearch className="h-4 w-4 flex-shrink-0 text-primary-500" aria-hidden="true" />
        <span className={`flex-1 truncate text-left text-sm ${urlCity || urlQuery ? 'text-ink' : 'text-ink-500'}`}>
          {mobileSummary}
        </span>
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-500 text-white" aria-hidden="true">
          <FiSearch className="h-4 w-4" />
        </span>
      </button>

      {/* ── Desktop pill (form, 3 segments) ── */}
      <form
        onSubmit={handleSubmit}
        className={`hidden items-center rounded-full border border-ink/15 bg-cream-50 transition-shadow md:flex ${compact ? 'h-12 p-1.5 shadow-sm hover:shadow' : 'p-2 shadow-lg hover:shadow-xl'}`}
      >
        {/* Segment 1 — Locație. Free-text input with an Airbnb-style
            popover that opens on focus: "Aproape de tine" geolocate
            row + eight canonical-city suggestion tiles, each with
            an emoji icon and a Petzy-context subtitle. Backend
            fuzzy-matches city contains (Cluj / Cluj-Napoca / Cluj
            Napoca all resolve), so the input ALSO accepts arbitrary
            free-text (Sighișoara, Tulcea, ...). */}
        <label
          ref={locRef}
          className="group relative flex flex-1 items-center gap-2 rounded-full px-5 py-2 transition-colors hover:bg-ink/5"
        >
          <FiMapPin className="h-4 w-4 flex-shrink-0 text-ink-300" aria-hidden="true" />
          <div className="flex min-w-0 flex-1 flex-col">
            <span className={`text-[11px] font-semibold uppercase tracking-wide text-ink-600 ${compact ? 'hidden' : ''}`}>
              Locație
            </span>
            <input
              type="text"
              value={draftCity}
              onFocus={() => setLocOpen(true)}
              onChange={(e) => {
                setDraftCity(e.target.value);
                // Typing invalidates the previous arrow-key highlight —
                // pressing Enter after edit should commit the typed
                // text, not the previously-highlighted option.
                setActiveIndex(-1);
              }}
              onKeyDown={(e) => {
                // Enter with no active option = commit the typed text
                // and close. With an active option, the hook intercepts
                // Enter and dispatches to popoverRef.activate(idx)
                // instead — its preventDefault keeps the form from
                // submitting through to handleSubmit.
                if (e.key === 'Enter' && activeIndex < 0) {
                  e.preventDefault();
                  commitParam('city', draftCity);
                  setLocOpen(false);
                  e.currentTarget.blur();
                  return;
                }
                onComboKeyDown(e);
              }}
              placeholder="Oriunde"
              className="w-full truncate border-none bg-transparent p-0 text-sm text-ink placeholder-ink-300 outline-none focus:ring-0"
              aria-label="Locație"
              // ARIA combobox pattern — see FilterModal's city input for
              // the full rationale. aria-controls links to the popover's
              // id below; LocationPopover renders role="option" rows.
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={locOpen}
              aria-haspopup="listbox"
              // Conditional aria-controls: see FilterModal's city
              // input for the rationale (axe-core flags a dangling
              // reference when the popover is closed).
              aria-controls={locOpen ? 'pill-city-listbox' : undefined}
              // activedescendant points at the highlighted option's id
              // — screen readers announce the option without DOM focus
              // leaving the textbox, so the visitor can keep typing
              // while navigating the suggestion list.
              aria-activedescendant={activeId}
              autoComplete="off"
            />
          </div>

          {locOpen && (
            <LocationPopover
              ref={popoverRef}
              id="pill-city-listbox"
              activeIndex={activeIndex}
              onActiveIndexChange={setActiveIndex}
              onPickCity={(name) => {
                setDraftCity(name);
                commitParam('city', name);
                setLocOpen(false);
              }}
              className="absolute left-0 top-full z-30 mt-3 w-80"
            />
          )}
        </label>

        <span className="h-7 w-px flex-shrink-0 bg-ink/10" aria-hidden="true" />

        {/* Segment 2 — Caută (free-text by salon name). Category filtering lives
            in the Filtre modal + the page pills now, not here. */}
        <label className="group flex flex-1 cursor-text items-center gap-2 rounded-full px-5 py-2 transition-colors hover:bg-ink/5">
          <FiType className="h-4 w-4 flex-shrink-0 text-ink-300" aria-hidden="true" />
          <div className="flex min-w-0 flex-1 flex-col">
            <span className={`text-[11px] font-semibold uppercase tracking-wide text-ink-600 ${compact ? 'hidden' : ''}`}>
              Caută
            </span>
            <input
              type="text"
              value={draftQuery}
              onChange={(e) => setDraftQuery(e.target.value)}
              placeholder="Nume salon"
              className="w-full truncate border-none bg-transparent p-0 text-sm text-ink placeholder-ink-300 outline-none focus:ring-0"
              aria-label="Caută"
            />
          </div>
        </label>

        <button
          type="submit"
          aria-label="Caută"
          className={`flex flex-shrink-0 items-center justify-center rounded-full bg-primary-500 text-white shadow-sm transition-colors hover:bg-primary-600 ${compact ? 'h-9 w-9' : 'h-11 w-11'}`}
        >
          <FiSearch className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}
